import {
  Layers,
  Sparkles,
  AlertTriangle,
  ShieldCheck,
  EyeOff,
  Fingerprint,
  RefreshCw,
  Clock,
  Camera,
  Clipboard,
  ScanFace,
  FileLock2,
  CreditCard,
  StickyNote,
  IdCard,
  Globe2,
} from "lucide-react";
import SectionHeading from "@/components/SectionHeading";

const itemTypes = [
  { icon: Globe2, name: "Logins", desc: "URL · username · password · 2FA notes" },
  { icon: CreditCard, name: "Cards", desc: "Card number · CVV · expiry, encrypted at rest" },
  { icon: IdCard, name: "Identities", desc: "Names, addresses, IDs — for fast autofill" },
  { icon: StickyNote, name: "Secure notes", desc: "Markdown notes, sealed individually" },
  { icon: FileLock2, name: "Documents", desc: "Attach passports, recovery codes, certificates" },
];

const featureGroups = [
  {
    title: "Vaulting",
    items: [
      { icon: Layers, t: "Multi-vault", d: "Separate work, personal and family vaults — each with its own master password." },
      { icon: EyeOff, t: "Decoy vault", d: "Boot a benign vault under coercion. The real one stays sealed." },
      { icon: RefreshCw, t: "Encrypted backup", d: "Export portable .avault bundles. Sync where you want, how you want." },
    ],
  },
  {
    title: "Generation & hygiene",
    items: [
      { icon: Sparkles, t: "Password generator", d: "Cryptographically strong passwords, passphrases, PINs and tokens." },
      { icon: AlertTriangle, t: "Duplicate detection", d: "Catch reused passwords across all your accounts." },
      { icon: ShieldCheck, t: "Optional breach check", d: "k-anonymous — only a 5-char hash prefix ever leaves your device." },
    ],
  },
  {
    title: "Locks & defenses",
    items: [
      { icon: Fingerprint, t: "Biometric unlock", d: "Touch ID, Face ID, Windows Hello, Android biometrics." },
      { icon: Clock, t: "Auto-lock", d: "Lock after inactivity, on app blur, on screen-off." },
      { icon: Camera, t: "Screenshot protection", d: "Block thumbnails, app-switcher previews and screenshots." },
      { icon: Clipboard, t: "Clipboard auto-clear", d: "Copy with confidence — clipboard is wiped on a timer." },
      { icon: ScanFace, t: "Stealth mode", d: "Hide app icon, mask field names, blur previews." },
    ],
  },
];

export default function Features() {
  return (
    <div>
      <section className="py-20 lg:py-24" data-testid="features-hero">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="label-eyebrow mb-5">Features</p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95]">
            Every secret. <span className="text-mint-600 dark:text-mint">One vault.</span>
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-lg text-muted-foreground leading-relaxed">
            Logins, cards, identities, notes and documents — all sealed locally with AES-256-GCM and your master password.
          </p>
        </div>
      </section>

      <section className="pb-20" data-testid="item-types-section">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {itemTypes.map((it) => (
            <div
              key={it.name}
              className="glass-card p-6 hover:-translate-y-1 transition-transform duration-300"
              data-testid={`item-type-${it.name.toLowerCase()}`}
            >
              <div className="h-10 w-10 rounded-xl bg-mint/40 dark:bg-mint/20 flex items-center justify-center text-mint-700 dark:text-mint">
                <it.icon className="h-5 w-5" />
              </div>
              <p className="mt-4 font-heading font-semibold">{it.name}</p>
              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{it.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {featureGroups.map((g, gi) => (
        <section key={g.title} className={`py-16 ${gi % 2 ? "" : ""}`} data-testid={`feature-group-${g.title.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="max-w-7xl mx-auto px-6">
            <SectionHeading eyebrow={`0${gi + 1} · ${g.title}`} title={g.title} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
              {g.items.map((f) => (
                <div
                  key={f.t}
                  className="glass-card p-7 hover:-translate-y-1 transition-all duration-300"
                  data-testid={`feature-${f.t.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="h-11 w-11 rounded-xl bg-foreground/5 flex items-center justify-center">
                    <f.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 font-heading text-xl font-semibold tracking-tight">{f.t}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}
